/**
 * Drag and Drop Handler Component
 * Adds actual drag-to-reorder functionality for components in content editor
 */

import { useEffect, useRef } from 'react';

interface DragDropHandlerProps {
  editorRef: React.RefObject<HTMLDivElement>;
  onContentChange: () => void;
  onBeforeMove?: () => void;
}

// Selectors for draggable elements (components)
const COMPONENT_SELECTORS = [
  '.learning-objectives',
  '.key-takeaways',
  '.learning-checkpoint',
  '.skillzen-code-snippet',
  '.example-box',
  '.callout',
  '.callout-tip',
  '.callout-warning',
  '.callout-note',
  '.callout-info',
];

// All draggable selectors including media and blocks
const DRAGGABLE_SELECTORS = [
  ...COMPONENT_SELECTORS,
  'table',
  'blockquote',
  'pre',
  'hr',
].join(', ');

// Media elements that need their parent container to be dragged
const MEDIA_SELECTORS = 'img, video, audio, iframe';

export default function DragDropHandler({ editorRef, onContentChange, onBeforeMove }: DragDropHandlerProps) {
  const draggedElementRef = useRef<HTMLElement | null>(null);
  const dragHandleRef = useRef<HTMLElement | null>(null);
  const deleteButtonRef = useRef<HTMLElement | null>(null);
  const placeholderRef = useRef<HTMLElement | null>(null);
  const ghostRef = useRef<HTMLElement | null>(null);
  const offsetRef = useRef({ x: 0, y: 0 });
  // Last known pointer position — the auto-scroll loop re-evaluates the drop target
  // from it, since scrolling alone fires no mousemove.
  const pointerRef = useRef({ x: 0, y: 0 });
  const autoScrollRafRef = useRef<number | null>(null);

  useEffect(() => {
    if (!editorRef.current) return;

    // Create delete button
    const createDeleteButton = (): HTMLElement => {
      const btn = document.createElement('button');
      btn.className = 'component-delete-btn-dynamic';
      btn.innerHTML = '×';
      btn.title = 'Delete';
      btn.type = 'button';
      // Keep the button INSIDE the component box. Sitting outside it (negative offsets)
      // got clipped whenever the component or an ancestor clips overflow — e.g.
      // .skillzen-code-snippet has `overflow: hidden`, and the scrolling editor pane
      // clips anything past its padding box — so the × showed up half-cut.
      btn.style.cssText = `
        position: absolute !important;
        top: 6px !important;
        right: 6px !important;
        width: 24px !important;
        height: 24px !important;
        background: #ef4444 !important;
        border: 2px solid white !important;
        border-radius: 50% !important;
        cursor: pointer !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        color: white !important;
        font-size: 16px !important;
        font-weight: bold !important;
        line-height: 1 !important;
        z-index: 101 !important;
        opacity: 0;
        transition: opacity 0.2s, background 0.2s;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2) !important;
        pointer-events: auto !important;
      `;
      return btn;
    };

    // Create drag handle
    const createDragHandle = (): HTMLElement => {
      const handle = document.createElement('div');
      handle.className = 'component-drag-handle';
      handle.innerHTML = `
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="8" cy="6" r="2"/>
          <circle cx="16" cy="6" r="2"/>
          <circle cx="8" cy="12" r="2"/>
          <circle cx="16" cy="12" r="2"/>
          <circle cx="8" cy="18" r="2"/>
          <circle cx="16" cy="18" r="2"/>
        </svg>
      `;
      handle.style.cssText = `
        position: absolute !important;
        top: 8px !important;
        left: 8px !important;
        width: 28px !important;
        height: 28px !important;
        background: #3b82f6 !important;
        border: 2px solid white !important;
        border-radius: 6px !important;
        cursor: grab !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        color: white !important;
        z-index: 100 !important;
        opacity: 0;
        transition: opacity 0.2s;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3) !important;
        flex-shrink: 0 !important;
        pointer-events: auto !important;
      `;
      handle.title = 'Drag to reorder';
      return handle;
    };

    // Create drop placeholder
    const createPlaceholder = (): HTMLElement => {
      const el = document.createElement('div');
      el.className = 'drop-placeholder';
      el.style.cssText = `
        height: 4px;
        background: linear-gradient(90deg, #3b82f6, #8b5cf6);
        border-radius: 2px;
        margin: 8px 0;
        box-shadow: 0 0 12px rgba(59, 130, 246, 0.6);
        pointer-events: none;
      `;
      return el;
    };

    // Create ghost element for dragging
    const createGhost = (element: HTMLElement): HTMLElement => {
      const ghost = element.cloneNode(true) as HTMLElement;
      ghost.style.cssText = `
        position: fixed;
        pointer-events: none;
        z-index: 10000;
        opacity: 0.8;
        transform: rotate(2deg);
        box-shadow: 0 10px 40px rgba(0,0,0,0.3);
        max-width: ${element.offsetWidth}px;
        max-height: 200px;
        overflow: hidden;
      `;
      // Remove any buttons/handles from ghost
      ghost.querySelectorAll('.component-drag-handle, .component-delete-btn, .component-delete-btn-dynamic, .move-buttons-container, .image-action-buttons, .resize-handle, .void-element-controls-wrapper').forEach(el => el.remove());
      // Remove outline from images in ghost
      ghost.querySelectorAll('img').forEach(img => {
        (img as HTMLElement).style.outline = 'none';
      });
      return ghost;
    };

    // Reference to wrapper for void elements (hr, img, etc.)
    let wrapperRef: HTMLElement | null = null;

    // Show drag handle and delete button on element
    const showDragHandle = (element: HTMLElement) => {
      hideDragHandle();

      // Check if element is a void element (cannot have children)
      const isVoidElement = ['HR', 'IMG', 'BR', 'INPUT'].includes(element.tagName);

      let targetElement = element;

      if (isVoidElement) {
        // For void elements, create a wrapper to hold the controls
        const wrapper = document.createElement('div');
        wrapper.className = 'void-element-controls-wrapper';
        wrapper.style.cssText = `
          position: relative !important;
          display: inline-block !important;
          width: 100% !important;
        `;

        // Insert wrapper before the element and move element inside
        element.parentNode?.insertBefore(wrapper, element);
        wrapper.appendChild(element);
        wrapperRef = wrapper;
        targetElement = wrapper;
      } else {
        // Ensure element has position relative
        const computed = window.getComputedStyle(element);
        if (computed.position === 'static') {
          element.style.position = 'relative';
        }
      }

      // Create and add drag handle
      const handle = createDragHandle();
      targetElement.appendChild(handle);
      dragHandleRef.current = handle;

      // Create and add delete button
      const deleteBtn = createDeleteButton();
      targetElement.appendChild(deleteBtn);
      deleteButtonRef.current = deleteBtn;

      // Fade in both
      requestAnimationFrame(() => {
        handle.style.opacity = '1';
        deleteBtn.style.opacity = '1';
      });

      // Handle drag start
      handle.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        // For void elements, we drag the wrapper
        startDrag(isVoidElement ? targetElement : element, e);
      });

      // Handle delete click
      deleteBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (onBeforeMove) {
          onBeforeMove(); // Save to history for undo
        }
        // For void elements, remove the wrapper (which contains the element)
        if (isVoidElement && wrapperRef) {
          wrapperRef.remove();
        } else {
          element.remove();
        }
        hideDragHandle();
        onContentChange();
      });

      // Hover effect on delete button
      deleteBtn.addEventListener('mouseenter', () => {
        deleteBtn.style.background = '#dc2626';
      });
      deleteBtn.addEventListener('mouseleave', () => {
        deleteBtn.style.background = '#ef4444';
      });
    };

    // Hide drag handle and delete button
    const hideDragHandle = () => {
      if (dragHandleRef.current) {
        dragHandleRef.current.remove();
        dragHandleRef.current = null;
      }
      if (deleteButtonRef.current) {
        deleteButtonRef.current.remove();
        deleteButtonRef.current = null;
      }
      // Unwrap void elements from their wrapper
      if (wrapperRef) {
        const voidElement = wrapperRef.querySelector('hr, img, br, input');
        if (voidElement && wrapperRef.parentNode) {
          wrapperRef.parentNode.insertBefore(voidElement, wrapperRef);
          wrapperRef.remove();
        }
        wrapperRef = null;
      }
    };

    // Start dragging
    const startDrag = (element: HTMLElement, e: MouseEvent) => {
      draggedElementRef.current = element;
      pointerRef.current = { x: e.clientX, y: e.clientY };

      // Calculate offset from mouse to element top-left
      const rect = element.getBoundingClientRect();
      offsetRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };

      // Style the original element
      element.style.opacity = '0.3';
      element.style.outline = '2px dashed #3b82f6';

      // Create ghost
      const ghost = createGhost(element);
      ghost.style.left = `${rect.left}px`;
      ghost.style.top = `${rect.top}px`;
      ghost.style.width = `${rect.width}px`;
      document.body.appendChild(ghost);
      ghostRef.current = ghost;

      // Create placeholder
      placeholderRef.current = createPlaceholder();

      // Hide the drag handle during drag
      hideDragHandle();

      // Change cursor
      document.body.style.cursor = 'grabbing';

      // Add listeners
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      startAutoScroll();
    };

    // Scrollable ancestors of the editor, innermost first. The editor pane itself scrolls
    // (max-h-[600px] overflow-auto) and sits inside another scroller (max-h-[85vh]), so the
    // auto-scroll below has to pick whichever can still move in the wanted direction.
    const getScrollHosts = (): HTMLElement[] => {
      const hosts: HTMLElement[] = [];
      let el: HTMLElement | null = editorRef.current;
      while (el) {
        const overflow_y = window.getComputedStyle(el).overflowY;
        if (/(auto|scroll|overlay)/.test(overflow_y) && el.scrollHeight > el.clientHeight + 1) {
          hosts.push(el);
        }
        el = el.parentElement;
      }
      return hosts;
    };

    // Scroll the editor when the pointer nears the top/bottom edge, so a component can be
    // dragged to a position that is off-screen (top -> bottom and back). Without this the
    // only valid drop targets are the ones currently visible.
    const EDGE_BAND = 64; // px from the edge where scrolling kicks in
    const MAX_SCROLL_STEP = 20; // px per frame at the very edge

    let last_scroll_signature = '';

    const autoScrollTick = () => {
      autoScrollRafRef.current = null;
      if (!draggedElementRef.current) return;

      const { x, y } = pointerRef.current;
      const hosts = getScrollHosts();

      for (const host of hosts) {
        const rect = host.getBoundingClientRect();

        // Distance into the edge band, 0..1 — scroll speed ramps up toward the edge.
        let direction = 0;
        let ratio = 0;
        if (y < rect.top + EDGE_BAND) {
          direction = -1;
          ratio = Math.min(1, (rect.top + EDGE_BAND - y) / EDGE_BAND);
        } else if (y > rect.bottom - EDGE_BAND) {
          direction = 1;
          ratio = Math.min(1, (y - (rect.bottom - EDGE_BAND)) / EDGE_BAND);
        }
        if (direction === 0) continue;

        // Already at the limit in that direction — let an outer scroller take over.
        if (direction < 0 && host.scrollTop <= 0) continue;
        if (direction > 0 && host.scrollTop + host.clientHeight >= host.scrollHeight - 1) continue;

        host.scrollTop += direction * Math.max(2, MAX_SCROLL_STEP * ratio);
        break;
      }

      // Any scroll (ours above, or the user's wheel) shifts the content under a stationary
      // pointer, and neither fires a mousemove — so re-place the drop marker when it changes.
      const signature = hosts.map(h => h.scrollTop).join(',');
      if (signature !== last_scroll_signature) {
        last_scroll_signature = signature;
        updateDropTarget(x, y);
      }

      autoScrollRafRef.current = requestAnimationFrame(autoScrollTick);
    };

    const startAutoScroll = () => {
      last_scroll_signature = '';
      if (autoScrollRafRef.current === null) {
        autoScrollRafRef.current = requestAnimationFrame(autoScrollTick);
      }
    };

    const stopAutoScroll = () => {
      if (autoScrollRafRef.current !== null) {
        cancelAnimationFrame(autoScrollRafRef.current);
        autoScrollRafRef.current = null;
      }
    };

    // Handle drag movement
    const handleDragMove = (e: MouseEvent) => {
      if (!draggedElementRef.current || !ghostRef.current || !editorRef.current) return;

      pointerRef.current = { x: e.clientX, y: e.clientY };

      // Get editor bounds
      const editorRect = editorRef.current.getBoundingClientRect();

      // Keep the ghost roughly over the editor, but let it reach both edges — clamping it
      // by its own height used to park it well short of the bottom, which made dragging
      // downwards feel stuck.
      const padding = 10;
      const ghostWidth = ghostRef.current.offsetWidth || 200;
      const ghostHeight = ghostRef.current.offsetHeight || 100;
      const keep_visible = 24; // px of the ghost that must stay inside the editor box

      let newX = e.clientX - offsetRef.current.x;
      let newY = e.clientY - offsetRef.current.y;

      // Constrain X
      newX = Math.max(editorRect.left - padding, Math.min(newX, editorRect.right - ghostWidth + padding));
      // Constrain Y
      newY = Math.max(
        editorRect.top - ghostHeight + keep_visible,
        Math.min(newY, editorRect.bottom - keep_visible)
      );

      ghostRef.current.style.left = `${newX}px`;
      ghostRef.current.style.top = `${newY}px`;

      updateDropTarget(e.clientX, e.clientY);
    };

    // Position the drop marker for a pointer position. Split out of handleDragMove so the
    // auto-scroll loop can reuse it while the pointer is stationary.
    const updateDropTarget = (clientX: number, clientY: number) => {
      if (!draggedElementRef.current || !ghostRef.current || !editorRef.current) return;

      const editorRect = editorRef.current.getBoundingClientRect();

      // Check if mouse is within editor bounds
      const isWithinEditor =
        clientX >= editorRect.left &&
        clientX <= editorRect.right &&
        clientY >= editorRect.top &&
        clientY <= editorRect.bottom;

      if (!isWithinEditor) {
        // Remove placeholder if outside editor
        placeholderRef.current?.remove();
        ghostRef.current.style.opacity = '0.4';
        return;
      }

      ghostRef.current.style.opacity = '0.8';

      // Find drop target
      // Temporarily hide ghost to get element underneath
      ghostRef.current.style.display = 'none';
      const elementBelow = document.elementFromPoint(clientX, clientY);
      ghostRef.current.style.display = '';

      if (!elementBelow || !editorRef.current.contains(elementBelow)) {
        placeholderRef.current?.remove();
        return;
      }

      // Find the closest draggable element or block element
      let dropTarget: HTMLElement | null = elementBelow as HTMLElement;
      while (dropTarget && dropTarget !== editorRef.current) {
        // Check if it's a draggable component
        if (dropTarget.matches(DRAGGABLE_SELECTORS)) {
          break;
        }
        // Check if it's a block element
        if (dropTarget.tagName === 'P' ||
            dropTarget.tagName === 'H1' || dropTarget.tagName === 'H2' || dropTarget.tagName === 'H3' ||
            dropTarget.tagName === 'UL' || dropTarget.tagName === 'OL' ||
            dropTarget.tagName === 'BLOCKQUOTE' || dropTarget.tagName === 'TABLE' ||
            dropTarget.tagName === 'HR') {
          break;
        }
        // Check if it's a DIV that's a direct child of the editor (likely a media container)
        if (dropTarget.tagName === 'DIV' && dropTarget.parentElement === editorRef.current) {
          break;
        }
        // Check if it's a media-containing DIV
        if (dropTarget.tagName === 'DIV' && dropTarget.querySelector('img, video, audio, iframe')) {
          break;
        }
        dropTarget = dropTarget.parentElement;
      }

      if (!dropTarget || dropTarget === editorRef.current || dropTarget === draggedElementRef.current) {
        return;
      }

      // Don't drop inside the dragged element
      if (draggedElementRef.current.contains(dropTarget)) {
        return;
      }

      // Keep the operation a REORDER: resolve the target up to a real sibling of the dragged
      // element, so the placeholder always lands in its original parent. Inserting next to
      // whatever block happened to be under the cursor moved the element into a foreign
      // parent — nested inside another component, or lifted out of the lesson's wrapper
      // container, where it rendered unstyled and looked like it had left the content item.
      const home_parent = draggedElementRef.current.parentNode;
      let sibling: HTMLElement | null = dropTarget;
      while (sibling && sibling.parentNode !== home_parent) {
        if (sibling === editorRef.current) {
          sibling = null;
          break;
        }
        sibling = sibling.parentElement;
      }

      if (!sibling || sibling === draggedElementRef.current) {
        // Not a valid reorder position — show it as a rejected drop instead of relocating.
        placeholderRef.current?.remove();
        ghostRef.current.style.opacity = '0.4';
        return;
      }
      dropTarget = sibling;

      // Determine insert position based on mouse position
      const targetRect = dropTarget.getBoundingClientRect();
      const midY = targetRect.top + targetRect.height / 2;

      if (placeholderRef.current) {
        placeholderRef.current.remove();

        if (clientY < midY) {
          dropTarget.parentNode?.insertBefore(placeholderRef.current, dropTarget);
        } else {
          dropTarget.parentNode?.insertBefore(placeholderRef.current, dropTarget.nextSibling);
        }
      }
    };

    // Handle drag end
    const handleDragEnd = () => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      stopAutoScroll();

      // Reset cursor
      document.body.style.cursor = '';

      // Remove ghost
      if (ghostRef.current) {
        ghostRef.current.remove();
        ghostRef.current = null;
      }

      // Move element to placeholder position. The parent check keeps this a reorder among
      // siblings — a placeholder anywhere else is treated as an invalid drop and discarded,
      // so the element stays where it was rather than being relocated out of its container.
      if (
        draggedElementRef.current &&
        placeholderRef.current?.parentNode &&
        placeholderRef.current.parentNode === draggedElementRef.current.parentNode
      ) {
        // Save state before move for undo support
        if (onBeforeMove) {
          onBeforeMove();
        }
        placeholderRef.current.parentNode.insertBefore(
          draggedElementRef.current,
          placeholderRef.current
        );
        onContentChange();
      }

      // Reset dragged element styles
      if (draggedElementRef.current) {
        draggedElementRef.current.style.opacity = '';
        draggedElementRef.current.style.outline = '';
      }

      // Remove placeholder
      placeholderRef.current?.remove();
      placeholderRef.current = null;

      draggedElementRef.current = null;
    };

    // Find the draggable container for media elements
    const findMediaContainer = (mediaElement: HTMLElement): HTMLElement | null => {
      // For media elements, find their block-level container
      let container = mediaElement.parentElement;
      while (container && container !== editorRef.current) {
        // If parent is a P, DIV (not the editor), or has specific wrapper classes
        if (container.tagName === 'P' ||
            (container.tagName === 'DIV' &&
             (container.style.margin || container.style.textAlign ||
              container.classList.contains('image-resize-wrapper') ||
              container.style.cssText.includes('margin')))) {
          return container;
        }
        container = container.parentElement;
      }
      // If no suitable container found, return the media element itself
      return mediaElement;
    };

    // Check if element or its container is draggable
    const findDraggableElement = (target: HTMLElement): HTMLElement | null => {
      // First check if it's a media element
      if (target.matches(MEDIA_SELECTORS)) {
        // Skip if it's inside an image-resize-wrapper being resized
        if (target.closest('.image-resize-wrapper')) {
          const wrapper = target.closest('.image-resize-wrapper') as HTMLElement;
          // Return the wrapper's parent (usually a P tag)
          return wrapper.parentElement && wrapper.parentElement !== editorRef.current
            ? wrapper.parentElement
            : wrapper;
        }
        return findMediaContainer(target);
      }

      // Then check for component/block selectors
      let element: HTMLElement | null = target;
      while (element && element !== editorRef.current) {
        if (element.matches(DRAGGABLE_SELECTORS)) {
          return element;
        }
        element = element.parentElement;
      }

      return null;
    };

    // Handle mouse over for showing drag handle
    const handleMouseOver = (e: MouseEvent) => {
      // Don't show handle while dragging
      if (draggedElementRef.current) return;

      const target = e.target as HTMLElement;

      // Skip if hovering over resize handles or action buttons
      if (target.closest('.resize-handle, .image-action-buttons, .image-resize-wrapper button')) {
        return;
      }

      const draggable = findDraggableElement(target);

      if (draggable) {
        // Don't re-add if already showing on this element
        if (dragHandleRef.current?.parentElement === draggable) {
          return;
        }
        showDragHandle(draggable);
      }
    };

    // Handle mouse out
    const handleMouseOut = (e: MouseEvent) => {
      // Don't hide while dragging
      if (draggedElementRef.current) return;

      const relatedTarget = e.relatedTarget as HTMLElement;

      // Don't hide if moving to the drag handle, delete button, or void element wrapper
      if (relatedTarget?.closest('.component-drag-handle, .component-delete-btn-dynamic, .void-element-controls-wrapper')) {
        return;
      }

      // Check if still within a draggable element
      if (relatedTarget && editorRef.current?.contains(relatedTarget)) {
        const draggable = findDraggableElement(relatedTarget);
        if (draggable) {
          return;
        }
      }

      // Delay hide to allow moving to handle or delete button
      setTimeout(() => {
        if (!dragHandleRef.current?.matches(':hover') &&
            !dragHandleRef.current?.parentElement?.matches(':hover') &&
            !deleteButtonRef.current?.matches(':hover') &&
            !wrapperRef?.matches(':hover')) {
          hideDragHandle();
        }
      }, 100);
    };

    const editor = editorRef.current;
    editor.addEventListener('mouseover', handleMouseOver);
    editor.addEventListener('mouseout', handleMouseOut);

    // Cleanup
    return () => {
      editor.removeEventListener('mouseover', handleMouseOver);
      editor.removeEventListener('mouseout', handleMouseOut);
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      stopAutoScroll();
      hideDragHandle();
      ghostRef.current?.remove();
      placeholderRef.current?.remove();
    };
  }, [editorRef, onContentChange, onBeforeMove]);

  return null;
}
